import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  CREATE_STORE, UPDATE_STORE, GET_STORE_BASIC, type ManageStoreBasicData,
} from '../../graphql/management'
import { Feedback, useFormFeedback, type UserError } from './Feedback'

// Create or edit a store (route param :id present = edit). Mirrors iOS
// StoreCreationView / StoreDetailsView. Settings is the freeform JSON object.
function asBool(v: unknown) { return v === true || v === 'true' }

export default function StoreEditor() {
  const { id } = useParams()
  const editing = Boolean(id)
  const navigate = useNavigate()
  const fb = useFormFeedback()

  const { data, loading } = useQuery<ManageStoreBasicData>(GET_STORE_BASIC, {
    variables: { id }, skip: !editing, fetchPolicy: 'cache-and-network',
  })
  const [createStore, { loading: creating }] = useMutation(CREATE_STORE)
  const [updateStore, { loading: updating }] = useMutation(UPDATE_STORE)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [settings, setSettings] = useState<Record<string, unknown>>({ shipping_enabled: true, tax_enabled: false, livestream_enabled: true })

  const store = data?.store
  useEffect(() => {
    if (!store) return
    setName(store.name)
    setDescription(store.description ?? '')
    setSettings(store.settings ?? {})
  }, [store])

  function setKey(k: string, v: unknown) { setSettings((s) => ({ ...s, [k]: v })) }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    fb.reset()
    try {
      if (editing) {
        const { data } = await updateStore({ variables: { id, name, description, settings } })
        if (!fb.fromUserErrors(data?.updateStore?.userErrors as UserError[] | undefined)) return
      } else {
        const { data } = await createStore({ variables: { name, description, settings } })
        if (!fb.fromUserErrors(data?.createStore?.userErrors as UserError[] | undefined)) return
      }
      navigate('/manage/stores')
    } catch (err) {
      fb.setError(err instanceof Error ? err.message : String(err))
    }
  }

  if (editing && loading && !store) return <div className="muted">Loading store…</div>
  const saving = creating || updating

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>{editing ? 'Edit store' : 'New store'}</h1>
        <button className="btn-link" onClick={() => navigate('/manage/stores')}>← Back</button>
      </header>

      <form className="create-form" onSubmit={onSubmit}>
        <label><span>Store name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label><span>Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </label>
        <fieldset className="toggle-group">
          <label className="toggle"><input type="checkbox" checked={asBool(settings.shipping_enabled)} onChange={(e) => setKey('shipping_enabled', e.target.checked)} /><span>Shipping enabled</span></label>
          <label className="toggle"><input type="checkbox" checked={asBool(settings.tax_enabled)} onChange={(e) => setKey('tax_enabled', e.target.checked)} /><span>Tax enabled</span></label>
          <label className="toggle"><input type="checkbox" checked={asBool(settings.livestream_enabled)} onChange={(e) => setKey('livestream_enabled', e.target.checked)} /><span>Livestreaming enabled</span></label>
        </fieldset>

        <Feedback error={fb.error} success={fb.success} />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save store' : 'Create store'}
          </button>
        </div>
      </form>
    </div>
  )
}
